import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link, useParams } from "react-router-dom";
import { LayoutContext } from "../layout/siteLayout";
import PageTop from "../layout/PageTop";
import withApi from "../higherOrderComponents/withApi";
import { DisplayGuid } from "../items";
import { COPENHAGEN_TIME_NOTE } from "../../common/copenhagenTime";
import { STATUS_HINT, cph, duration } from "../../next/pages/DeviceJobsShared";
import { JobStatus } from "./DeviceJobs";

// One booking/cancellation run on a device. Classic twin of /next/device-job/:id,
// read-only: status and meaning first, then timings, the script's steps
// ({ step, message, timestamp }), logs, result and the request it received.

const ACTIVE = ["Queued", "Claimed"];

const pretty = (value) => (typeof value === "string" ? value : JSON.stringify(value, null, 2));

const preStyle = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  background: "#f6f6f6",
  padding: ".75rem",
  borderRadius: "4px",
  maxHeight: "40rem",
  overflow: "auto",
  fontSize: ".8rem",
};

const FlowTable = ({ flow }) => {
  const start = flow.find((x) => x && x.timestamp)?.timestamp;

  return (
    <article className="data-table-wrap">
      <table className="data-lists component-data-table cell-content-align-top">
        <thead>
          <tr>
            <td>#</td>
            <td>Step</td>
            <td>Message</td>
            <td>At</td>
          </tr>
        </thead>
        <tbody>
          {flow.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={index}>
              <td>{index + 1}</td>
              {item && typeof item === "object" && item.step ? (
                <>
                  <td><strong>{item.step}</strong></td>
                  <td style={{ wordBreak: "break-word" }}>{item.message || "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {item.timestamp ? `+${duration(start, item.timestamp) || "0 s"}` : "—"}
                  </td>
                </>
              ) : (
                <td colSpan={3}><pre style={preStyle}>{pretty(item)}</pre></td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </article>
  );
};

FlowTable.propTypes = { flow: PropTypes.array.isRequired };

const DeviceJobInternal = ({ api }) => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.deviceJobs.getDeviceJob(jobId);
      if (!data || !data.id) {
        setFailed(true);
        return;
      }
      setJob(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, jobId]);

  useEffect(() => {
    load();
  }, [load]);

  // A job still in the queue or on the device changes by itself: follow it.
  useEffect(() => {
    if (!job || !ACTIVE.includes(job.status)) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 5000);
    return () => clearInterval(timer);
  }, [job, load]);

  const back = <p><Link to="/device-jobs">← All device jobs</Link></p>;

  const body = () => {
    if (failed) {
      return (
        <div className="alert alert-danger">
          Could not load this device job.{" "}
          <button type="button" className="btn btn-sm btn-secondary" onClick={load}>Retry</button>
        </div>
      );
    }
    if (!job) return <span>Loading…</span>;

    const result = job.result && typeof job.result === "object" ? job.result : null;
    const flow = Array.isArray(result?.flow) ? result.flow : null;
    const notYet = job.finishedAt ? "" : " yet";

    const facts = [
      ["Status", <JobStatus status={job.status} />],
      ["Action", job.action],
      ["Script", job.script],
      ["Venue", job.facilityName || job.productName || "—"],
      ["Product", job.productName || "—"],
      ["System", job.systemType || "—"],
      ["Integration", job.productId
        ? <Link to={`/product/${job.productId}/integration`}>{job.integrationId}</Link>
        : <DisplayGuid>{job.integrationId}</DisplayGuid>],
      ["Signup", job.signupId ? <DisplayGuid baseAnchor="signup">{job.signupId}</DisplayGuid> : "—"],
      ["User", job.userEmail || "—"],
      ["Activity starts", job.activityStartsAt ? cph(job.activityStartsAt) : "—"],
      ["Created", cph(job.createdAt)],
      ["Claimed", job.claimedAt ? `${cph(job.claimedAt)} by ${job.claimedBy || "?"}` : "not claimed"],
      ["Finished", job.finishedAt ? cph(job.finishedAt) : "—"],
      ["Waited for a runner", duration(job.createdAt, job.claimedAt) || "—"],
      ["Ran for", duration(job.claimedAt, job.finishedAt) || "—"],
      ["Script time", result?.seconds != null ? `${result.seconds} s` : "—"],
      ["Reference", job.reference || "—"],
      ["Pincode", job.pincode || "—"],
      ["Money spent", result?.moneySpent != null && result.moneySpent !== "" ? String(result.moneySpent) : "—"],
      ["Card used", result?.cardUsed || "—"],
      ["Dry run", result ? (result.dryRun ? "Yes" : "No") : "—"],
    ];

    return (
      <>
        <p style={{ color: "#666" }}>
          <code>{job.id}</code>
          {job.signupId && (
            <>
              {" · "}
              <Link to={`/device-jobs?signupId=${job.signupId}`}>Other jobs of this signup</Link>
            </>
          )}
          {" · "}
          <Link to={`/device-jobs?integrationId=${job.integrationId}`}>Jobs of this integration</Link>
        </p>

        {job.status === "Unknown" && (
          <div className="alert alert-warning"><strong>Unknown.</strong> {STATUS_HINT.Unknown}</div>
        )}
        {ACTIVE.includes(job.status) && (
          <div className="alert alert-info">{STATUS_HINT[job.status]} This page refreshes by itself every 5 seconds.</div>
        )}
        {job.error && <div className="alert alert-danger"><strong>Error:</strong> {job.error}</div>}

        <article className="data-table-wrap">
          <table className="data-lists component-data-table cell-content-align-top">
            <tbody>
              {facts.map(([label, value]) => (
                <tr key={label}>
                  <td style={{ width: "14rem" }}><strong>{label}</strong></td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <br />
        {(result?.screenshotUrl || result?.screenshot || result?.pageSourceUrl) && (
          <>
            <h3>Phone screen at the failure</h3>
            <p>
              {result.screenshotUrl && <a href={result.screenshotUrl} target="_blank" rel="noopener noreferrer">Screenshot</a>}
              {result.screenshotUrl && result.pageSourceUrl && " · "}
              {result.pageSourceUrl && <a href={result.pageSourceUrl} target="_blank" rel="noopener noreferrer">Page source (XML)</a>}
              {result.pageSourceUrl && ": every element on the screen, with label, position and visibility."}
            </p>
            {result.screenshotUrl
              ? (
                <a href={result.screenshotUrl} target="_blank" rel="noopener noreferrer">
                  <img src={result.screenshotUrl} alt="Phone screen when the script failed" style={{ maxWidth: "320px", width: "100%", border: "1px solid #ddd" }} />
                </a>
              )
              : result.screenshot && <p>Screenshot not uploaded: only on the runner Mac, at <code>{result.screenshot}</code> (deleted by macOS after a few days).</p>}
            <br />
          </>
        )}
        <h3>Flow</h3>
        {flow && flow.length > 0 ? <FlowTable flow={flow} /> : <p>No steps reported{notYet}.</p>}

        <h3>Logs</h3>
        {job.logs ? <pre style={preStyle}>{job.logs}</pre> : <p>No logs reported{notYet}.</p>}

        <h3>Result</h3>
        {job.result != null ? <pre style={preStyle}>{pretty(job.result)}</pre> : <p>No result reported{notYet}.</p>}

        <h3>Request</h3>
        <p style={{ color: "#666" }}>
          What the script received, without the account credentials (added by the backend at claim time, never stored).
        </p>
        {job.request != null ? <pre style={preStyle}>{pretty(job.request)}</pre> : <p>No request stored.</p>}

        <p style={{ color: "#888", marginTop: "1.5rem" }}>{COPENHAGEN_TIME_NOTE}</p>
      </>
    );
  };

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <PageTop documentTitle="Device job" documentDescription="A booking run on a device" />
          <h2 className="main-page-header">
            {job ? `${job.action} on device ` : "Device job "}
            {job && <JobStatus status={job.status} />}
          </h2>
          {back}
          {body()}
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

DeviceJobInternal.propTypes = {
  api: PropTypes.object.isRequired,
};

const DeviceJob = withApi(DeviceJobInternal);

DeviceJob.requiresLogin = true;

export default DeviceJob;
